import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, defaultIfEmpty, map, switchMap } from 'rxjs';

import { ErrorHandlerService } from '../../services/error-handler/error-handler.base-service';
import { NotificationsFacade } from '../../services/notifications/notifications.facade';
import * as fromActions from './notifications.actions';

@Injectable()
export class NotificationsEffects {
  public registerDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.RegisterDevice),
      switchMap(({ registerDeviceInput }) =>
        this.notificationsFacade.registerDevice(registerDeviceInput).pipe(
          map(success => fromActions.RegisterDeviceSuccess({ success })),
          /*
           * AuthInterceptor blocks a token-less GraphQL request by returning
           * EMPTY, which completes the Apollo observable without a value. Without
           * a fallback neither a success nor a failure action is dispatched, so
           * `registerDeviceActionState.pending` stays true for the rest of the
           * process -- and `registerDeviceWithToken`, which waits for it to clear,
           * silently drops every later registration. ART-19930
           */
          defaultIfEmpty(
            fromActions.RegisterDeviceFailed({
              error: new Error('Register device request completed without a response'),
            })
          ),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on notifications.effects.ts:registerDevice$:',
              error
            );
            return [fromActions.RegisterDeviceFailed({ error })];
          })
        )
      )
    );
  });

  public unregisterDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.UnregisterDevice),
      switchMap(({ identifier }) =>
        this.notificationsFacade.unregisterDevice(identifier).pipe(
          map(success => fromActions.UnregisterDeviceSuccess({ success })),
          // Same guarantee as above: logout waits on this action state, so it
          // must always resolve. ART-19930
          defaultIfEmpty(
            fromActions.UnregisterDeviceFailed({
              error: new Error('Unregister device request completed without a response'),
            })
          ),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on notifications.effects.ts:unregisterDevice$:',
              error
            );
            return [fromActions.UnregisterDeviceFailed({ error })];
          })
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private notificationsFacade: NotificationsFacade,
    private errorHandlerService: ErrorHandlerService
  ) {}
}
